import { useCallback, useRef, useState, type DragEvent } from "react";

import { KAI_ACCEPT } from "@/lib/kaiAttachments";

/** Accepted extensions derived from the shared `KAI_ACCEPT` allowlist. */
const ACCEPTED_EXTENSIONS = KAI_ACCEPT.split(",").map((e) =>
  e.trim().toLowerCase(),
);

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/** Whether a drag event is carrying files (as opposed to text/links). */
function dragHasFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes("Files");
}

export interface KaiFileDropHandlers {
  onDragEnter: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
}

export interface UseKaiFileDrop {
  /** True while a file drag is hovering the drop target. */
  isDragging: boolean;
  /** Spread onto the container that should catch dropped files. */
  dropHandlers: KaiFileDropHandlers;
}

/**
 * Catch files dragged from the OS/file explorer and dropped anywhere on the
 * Kai chat surface. Filters to the accepted document types and forwards them to
 * the same handler the attach button uses. Uses a drag counter so nested
 * children don't prematurely clear the drag state.
 */
export function useKaiFileDrop({
  onFiles,
  disabled,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}): UseKaiFileDrop {
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (disabled || !dragHasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (disabled || !dragHasFiles(e)) return;
      // Required for the drop event to fire; also signals a copy operation.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    },
    [disabled],
  );

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      if (disabled || !dragHasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragging(false);
    },
    [disabled],
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);

      const dropped = e.dataTransfer?.files
        ? Array.from(e.dataTransfer.files)
        : [];
      const accepted = dropped.filter(isAcceptedFile);
      if (accepted.length > 0) onFilesRef.current(accepted);
    },
    [disabled],
  );

  return {
    isDragging,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
